import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  getFileExtension,
  getFileIconByExtension,
} from '@nxsys/helpers/FileHelper';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { AppState } from '@nxsys/store';
import { formatDateDisplay } from '@nxsys/utils/dateUtils';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetEmployeeDocumentNearingExpiryListParams } from '@nxsys/store/slices/dashboard/types';
import { Icons } from '@nxsys/types/icons';
import { DocumentType } from '@nxsys/types/models/document';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

type Props = {
  subcontractorId: string;
};

const DocumentNearingExpiry: React.FC<Props> = ({ subcontractorId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const { employeeDocumentNearingExpiryList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } =
    employeeDocumentNearingExpiryList;

  // Calculate expiry date range: from today to 3 days from now
  const expiryDateFrom = useMemo(() => {
    return moment().startOf('day').toISOString();
  }, []);

  const expiryDateTo = useMemo(() => {
    return moment().add(3, 'days').endOf('day').toISOString();
  }, []);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (
    params: GetEmployeeDocumentNearingExpiryListParams,
  ) => {
    const newParams: GetEmployeeDocumentNearingExpiryListParams = {
      ...params,
      subcontractorId: subcontractorId,
      expiryDateFrom: expiryDateFrom,
      expiryDateTo: expiryDateTo,
    };
    dispatch(dashboardActions.getEmployeeDocumentNearingExpiryList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<DocumentType> = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 200,
    },
    {
      title: 'Attachment',
      key: 'documentTitle',
      render: (value) => (
        <div
          className="flex gap-2 cursor-pointer"
          onClick={() => window.open(value.documentFile, '_blank')}
        >
          {getFileIconByExtension(getFileExtension(value.documentFile))}
          <span>{value.documentTitle}</span>
        </div>
      ),
    },
    {
      title: 'Document Date',
      dataIndex: 'documentDate',
      key: 'documentDate',
      align: 'right',
      render: (value) => (
        <span>{formatDateDisplay(value, true, 'DD/MM/YYYY', '-')}</span>
      ),
      width: 220,
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      align: 'right',
      render: (value) => (
        <span>{formatDateDisplay(value, true, 'DD/MM/YYYY', '-')}</span>
      ),
      width: 220,
    },
  ];

  return (
    <AppCard className="h-full">
      <div className="p-[20px] flex justify-between items-center gap-x-[16px]">
        <div>
          <div className="text-[16px] font-[500] mb-[6px]">
            Documents Nearing Expiry
          </div>
          <div className="text-[12px] font-[400] text-neutral-500">
            Please upload updated versions of these documents before their
            expiry date to avoid disruption in your payments.
          </div>
        </div>
        <AppButton
          name="View Detail"
          variant="outlined"
          leftIcon={<Icons.IconEye />}
          onClick={() => navigate('/employee-documents')}
        />
      </div>
      <AppTableContainer
        rowKey="id"
        data={list}
        loading={loading}
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        pagination={false}
        scroll={{ x: 1200 }}
        columns={columns}
        onChangePagination={onChangePagination}
      />
    </AppCard>
  );
};

export default DocumentNearingExpiry;
